/**
 * @module cercaliagl/service/Pois
 */

import Service from './Service';

/**
 * @typedef {Object} PoisOptions
 * @property {import('../Bounds').default|undefined} bounds Bounds to search categories inside
 * @property {number|undefined} buffer Buffer size for search
 * @property {Array.<string>|undefined} categories List of categories to obtain. The 'pois' option must be null.
 * @property {number|undefined} gridsize Gridsize
 * @property {number|undefined} height Height in pixels.
 * @property {Array.<string>|undefined} pois POIs IDs list to obtain. If this option is included, the categories property will be ignored.
 * Does not apply any other constructor options.
 * @property {boolean|undefined} searchByScale Specify if you need to group POIs depending on parameters: bounds, width and height.
 * @property {number|undefined} width Width, in pixels.
 * @property {string|undefined} wkt Get all the POIs from selected categories, that intersects a polygon, in WKT format
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const poisService = new cercaliagl.service.Pois({ ... });
 * ```
 *
 * Service to obtain POIs. These POIs can be requested by POIs or category IDs.
 * The POIs will also be obtained given a geographic extension or a geometry in WKT format.<br/>
 * If you need categories IDs or you want to publish later to get these POIs through the API, please contact support.<br/>
 * More information can be found below in the constructor options.
 *
 * Example usage:
 *
 * ```js
 * const poisService = new cercaliagl.service.Pois({
 *   categories: ['DOOCAM'],
 *   bounds: map.getBounds(),
 * });
 * poisService.doPoisRequest((data) => {
 *   if (data.cercalia.map.getpoicats && data.cercalia.map.getpoicats.poilist && data.cercalia.map.getpoicats.poilist.poi) {
 *     const pois = data.cercalia.map.getpoicats.poilist.poi
 *     pois.forEach((poi) => {
 *       console.log(poi.id, poi.name.value, poi.info.value); // Print POI ID, name and info values.
 *     });
 *   }
 * });
 * ```
 *
 * @extends {Service}
 * @api
 */
class Pois extends Service {
  /**
   * @param {PoisOptions} [options] Opciones Geocoding
   */
  constructor(options = {}) {
    super();

    /**
     * @private
     * @type {Array.<string>}
     */
    this.pois_ = options.pois ? options.pois : [];

    /**
     * @private
     * @type {Array.<string>}
     */
    this.categories_ = options.categories ? options.categories : [];

    /**
     * @private
     * @type {string|null}
     */
    this.wkt_ = options.wkt ? options.wkt : null;

    /**
     * @private
     * @type {number|null}
     */
    this.buffer_ = options.buffer ? options.buffer : null;

    /**
     * @private
     * @type {import('../Bounds').default|null}
     */
    this.bounds_ = options.bounds ? options.bounds : null;

    /**
     * @private
     * @type {boolean}
     */
    this.searchByScale_ = options.searchByScale ? options.searchByScale : false;

    /**
     * @private
     * @type {number|null}
     */
    this.width_ = options.width ? options.width : null;

    /**
     * @private
     * @type {number|null}
     */
    this.height_ = options.height ? options.height : null;

    /**
     * @private
     * @type {number|null}
     */
    this.gridsize_ = options.gridsize ? options.gridsize : null;

    /**
     * @private
     * @type {XMLHttpRequest|null}
     */
    this.lastXhr_ = null;

    /**
     * @private
     * @type {import('../servers').PoisResult|null}
     */
    this.response_ = null;
  }

  /**
   * Returns the POIs list
   * @return {Array.<string>} Returns a POI IDs array
   * @api
   */
  getQueryPois() {
    return this.pois_;
  }

  /**
   * Set the POIs list to search.
   * @param {Array.<string>} categories POIs ID list, in an array
   * @api
   */
  setQueryCategories(categories) {
    this.categories_ = categories;
  }

  /**
   * Returns the POIs categories to search
   * @return {Array.<string>} categories list string
   * @api
   */
  getQueryCategories() {
    return this.categories_;
  }

  /**
   * Set the POIs categories to search.
   * @param {Array.<string>} categories categories list string
   * @api
   */
  setQueryPois(categories) {
    this.categories_ = categories;
  }

  /**
   * Returns the map area corresponding to the spatial query. The format is WKT
   * @return {string|null} Geometria WKT (string)
   * @api
   */
  getWkt() {
    return this.wkt_;
  }

  /**
   * Set the polygon for the spatial query. String WKT format
   * If spatial query use a WKT, the map bounds will be ignored and must be reset next time
   * @param {string} wkt WKT geometry
   * @api
   */
  setWkt(wkt) {
    this.bounds_ = null;
    this.wkt_ = wkt;
  }

  /**
   * Returns the WKT of the spatial query
   * @return {import('../Bounds').default|null} Coordinate
   * @api
   */
  getBounds() {
    return this.bounds_;
  }

  /**
   * Set the polygon for the spatial query. String WKT format.
   * If spatial query use map bounds, the WKT will be ignored and must be reset next time
   * @param {import('../Bounds').default} bounds Bounds
   * @api
   */
  setBounds(bounds) {
    this.wkt_ = null;
    this.bounds_ = bounds;
  }

  /**
   * Clear response.
   * @api
   */
  clearResponse() {
    this.response_ = null;
  }

  /**
   * Returns the response with the 'geocode' function request
   * @return {import('../servers').PoisResult|null} response
   * @api
   */
  getResponse() {
    return this.response_;
  }

  /**
   * @param {boolean} searchByScale Enable search by scale. `true` or `false`.
   * @api
   */
  setSearchbyScale(searchByScale) {
    this.searchByScale_ = searchByScale;
  }

  /**
   * @return {boolean} boolean Return if shows if POIs by scale search is used. `true` or `false`.
   * @api
   */
  isSearchbyScale() {
    return this.searchByScale_;
  }

  /**
   * @return {number|null} width
   * @api
   */
  getWidth() {
    return this.width_;
  }

  /**
   * @param {number} width Width in pixels.
   * @api
   */
  setWidth(width) {
    this.width_ = width;
  }

  /**
   * @return {number|null} height Height in pixels
   * @api
   */
  getHeigth() {
    return this.height_;
  }

  /**
   * Set height
   * @param {number} height Height in pixels
   * @api
   */
  setHeight(height) {
    this.height_ = height;
  }

  /**
   * @return {number|null} gridsize Gridsize in pixels.
   * @api
   */
  getGridsize() {
    return this.gridsize_;
  }

  /**
   * @param {number} gridsize Gridsize in pixels.
   * @api
   */
  setGridsize(gridsize) {
    this.gridsize_ = gridsize;
  }

  /**
   * Execute the server request and the callback function to be processed with the response
   * @param {Function} callbackFn Funcion callback to parse server data's response.
   * @api
   */
  doPoisRequest(callbackFn) {
    let params;
    // Si buscamos por categoria
    if (this.categories_.length > 0) {
      // Queremos obtener por extension (extent o bounds)
      if (this.bounds_) {
        const pointNW = this.bounds_.getNorthWest();
        const pointSE = this.bounds_.getSouthEast();

        const extTopLeft = `${pointNW.getLat()},${pointNW.getLon()}`;
        const extBottomRight = `${pointSE.getLat()},${pointSE.getLon()}`;

        // Obtener todos los POIs acorde a una extension. Sin limitarlos dependiendo la escala
        // Puede devolver error del servidor (o tardar mucho en caso que se devuelvan demasiados).
        if (!this.searchByScale_) {
          params = {
            cmd: 'pois',
            searchIn: 'bounds',
            getpoicats: this.categories_.join(','),
            extent: `${extTopLeft}|${extBottomRight}`,
          };

          this.ajaxQuery_(params, callbackFn);
          // Devuelve una cantidad de POIs acorde con la extension.
        } else {
          params = {
            cmd: 'pois',
            searchIn: 'bounds',
            gpoicats: this.categories_.join(','),
            extent: `${extTopLeft}|${extBottomRight}`,
            width: this.width_,
            height: this.height_,
          };

          if (this.gridsize_) {
            params.gridsize = this.gridsize_;
          }

          this.ajaxQuery_(params, callbackFn);
        }

        // Devuelve todos los POIs de las categorias especificadas que se encuentran dentro un poligono (wkt)
      } else if (this.wkt_) {
        params = {
          cmd: 'pois',
          searchIn: 'wkt',
          getpoicats: this.categories_.join(','),
          wkt: this.wkt_,
        };

        if (this.buffer_) {
          params.buffer = this.buffer_;
        }

        this.ajaxQuery_(params, callbackFn);
      }
      // Devuelve los POIs especificados dentro del Array 'this.pois_'
    } else if (this.pois_.length > 0) {
      params = {
        cmd: 'pois',
        poilist: this.pois_.join(','),
      };
      this.ajaxQuery_(params, callbackFn);
    }
  }

  /**
   * @private
   * @param {!Object} params Data parameters
   * @param {Function|null} callbackFn Callback Function
   */
  ajaxQuery_(params, callbackFn) {
    const reqParams = {
      data: params,
      timeout: 20000,
      callbackFn: (data) => {
        this.response_ = data;
        if (callbackFn) {
          callbackFn(data);
        } else {
          console.error('No callbackfunction defined'); // eslint-disable-line no-console
        }
      },
    };
    if (!this.wkt) {
      this.getRequest(reqParams);
    } else {
      this.postRequest(reqParams);
    }
  }
}

export default Pois;
